import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Globe, BookMarked, Save, X, Sparkles, Loader2, ExternalLink } from 'lucide-react';

/**
 * CourseResearchModal — admin research tool paired with each course.
 *
 * Uses the course abstract (or a manual override) to:
 *   1) Retrieve top-K matches from the vetted Knowledge Base.
 *   2) Ask Claude Sonnet 5 (with the built-in web_search tool) for a
 *      complementary briefing from the live web.
 *
 * The admin reviews both sides, can tweak the briefing, and one click
 * saves the approved briefing back into the KB as a new document. All
 * future RAG answers (Ask Expert, /api/ai/ask, lesson citations) will
 * then include that vetted evidence — closing the KB loop cleanly.
 */
const CourseResearchModal = ({ course, onClose }) => {
  const { API } = useContext(AuthContext);
  const [override, setOverride] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);   // { query, kb_hits, web_answer, web_sources }
  const [editableTitle, setEditableTitle] = useState('');
  const [editableContent, setEditableContent] = useState('');
  const [selectedUrls, setSelectedUrls] = useState([]);

  const hasAbstract = !!(course?.abstract && course.abstract.trim());

  const runResearch = async () => {
    setLoading(true);
    setResult(null);
    try {
      const body = override.trim() ? { query_override: override.trim() } : {};
      const { data } = await axios.post(
        `${API}/admin/lms/courses/${course.id}/research`,
        body,
      );
      setResult(data);
      setEditableTitle(`Research: ${course.title}`);
      setEditableContent(data.web_answer || '');
      setSelectedUrls(data.web_sources || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Research failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleUrl = (u) => {
    setSelectedUrls((prev) =>
      prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u],
    );
  };

  const saveToKB = async () => {
    if (!editableContent.trim() || editableContent.trim().length < 20) {
      toast.error('Briefing needs at least 20 characters before saving.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/lms/courses/${course.id}/research/save-to-kb`,
        {
          title: editableTitle.trim() || `Research: ${course.title}`,
          content: editableContent,
          source_urls: selectedUrls,
        },
      );
      toast.success(`Saved to KB — ${data.chunk_count} chunks indexed.`);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Save to KB failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="course-research-modal"
    >
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl max-w-4xl w-full my-8 border border-border">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" /> Research assistant
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              KB first · live web second · admin decides what enters the library
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="course-research-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          {/* Course context */}
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
            <div className="font-semibold">{course.title}</div>
            {hasAbstract ? (
              <div className="mt-1 text-muted-foreground line-clamp-3">
                {course.abstract}
              </div>
            ) : (
              <div className="mt-1 text-amber-600 text-xs">
                No abstract set on this course. Add one or use the override
                below.
              </div>
            )}
          </div>

          {/* Override */}
          <div>
            <Label className="text-sm">
              Optional override query (leave blank to use the abstract)
            </Label>
            <Input
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="e.g. 'raw honey freeze-drying temperature protocol 2025'"
              data-testid="course-research-override"
            />
            <Button
              onClick={runResearch}
              disabled={loading || (!hasAbstract && !override.trim())}
              className="mt-3 bg-blue-600 hover:bg-blue-700"
              data-testid="course-research-run"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Search KB + web</>
              )}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-5">
              {/* KB hits */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <BookMarked className="w-4 h-4 text-emerald-600" />
                  Vetted KB matches
                  <span className="text-xs font-normal text-muted-foreground">
                    ({result.kb_hits?.length || 0})
                  </span>
                </h3>
                {result.kb_hits?.length ? (
                  <div className="space-y-2">
                    {result.kb_hits.map((h) => (
                      <div
                        key={h.source_id + h.snippet.slice(0, 20)}
                        className="rounded-md border border-emerald-200/60 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800/40 p-2 text-sm"
                        data-testid="course-research-kb-hit"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-medium">{h.source_title}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            score {h.score.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {h.snippet}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-amber-600">
                    No KB matches — this topic is a gap. The web briefing below
                    is your starting point.
                  </p>
                )}
              </div>

              {/* Web briefing (editable) */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-blue-600" /> Fresh web briefing
                </h3>
                <Label className="text-xs">Title (used as KB document title)</Label>
                <Input
                  value={editableTitle}
                  onChange={(e) => setEditableTitle(e.target.value)}
                  className="mb-2"
                  data-testid="course-research-title"
                />
                <Label className="text-xs">Briefing (edit before saving)</Label>
                <textarea
                  value={editableContent}
                  onChange={(e) => setEditableContent(e.target.value)}
                  rows={10}
                  className="w-full p-3 border border-input rounded-md text-sm font-mono bg-background"
                  data-testid="course-research-content"
                />
                {result.web_sources?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold mb-1">
                      Sources to save with this briefing:
                    </p>
                    <div className="space-y-1">
                      {result.web_sources.map((u) => (
                        <label
                          key={u}
                          className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUrls.includes(u)}
                            onChange={() => toggleUrl(u)}
                            className="mt-0.5"
                          />
                          <span className="break-all flex-1">{u}</span>
                          <a
                            href={u}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3 inline" />
                          </a>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2 border-t border-border">
                <Button
                  onClick={saveToKB}
                  disabled={saving || !editableContent.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="course-research-save-kb"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save briefing to KB</>
                  )}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseResearchModal;
